import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import 'leaflet/dist/leaflet.css';
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from 'react-leaflet';
import { Star, BedDouble, Bath, Maximize2, ExternalLink } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// AZ city → lat/lng lookup
const CITY_COORDS = {
  scottsdale: [33.4942, -111.9261],
  tempe:      [33.4255, -111.9400],
  mesa:       [33.4152, -111.8315],
  phoenix:    [33.4484, -112.0740],
  chandler:   [33.3062, -111.8413],
  gilbert:    [33.3528, -111.7890],
  peoria:     [33.5806, -112.2374],
  glendale:   [33.5387, -112.1860],
};

function getCoords(city) {
  if (!city) return null;
  const key = city.toLowerCase().trim();
  const exact = CITY_COORDS[key];
  if (exact) return exact;
  // Partial match
  const partial = Object.keys(CITY_COORDS).find(k => k.includes(key) || key.includes(k));
  return partial ? CITY_COORDS[partial] : null;
}

function scoreColor(score) {
  if (score == null) return '#94A3B8';
  if (score >= 80) return '#10B981';
  if (score >= 60) return '#F59E0B';
  return '#94A3B8';
}

// Fit bounds to visible markers
function FitBounds({ markers }) {
  const map = useMap();
  useEffect(() => {
    if (!markers.length) return;
    const L = require('leaflet');
    const bounds = L.latLngBounds(markers.map(m => m.coords));
    map.fitBounds(bounds, { padding: [60, 60] });
  }, [markers]); // eslint-disable-line
  return null;
}

export default function MapView() {
  const { authFetch } = useAuth();
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all | green | amber | gray

  useEffect(() => {
    authFetch(`${API}/api/listings/discover`)
      .then(r => r.ok ? r.json() : [])
      .then(data => { setListings(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []); // eslint-disable-line

  // Build markers from listings with valid coords
  const markers = listings
    .map(l => ({ listing: l, coords: getCoords(l.city) }))
    .filter(m => m.coords != null);

  const filtered = markers.filter(m => {
    const s = m.listing.match_score;
    if (filter === 'green') return s >= 80;
    if (filter === 'amber') return s >= 60 && s < 80;
    if (filter === 'gray')  return s == null || s < 60;
    return true;
  });

  const FILTERS = [
    { key: 'all',   label: 'All',         color: '#0A192F' },
    { key: 'green', label: '80%+ Match',  color: '#10B981' },
    { key: 'amber', label: '60-79%',       color: '#F59E0B' },
    { key: 'gray',  label: 'Under 60%',   color: '#94A3B8' },
  ];

  return (
    <div className="flex flex-col" style={{ height: 'calc(100vh - 64px)' }}>
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-3 bg-white border-b border-slate-100 shadow-sm flex-shrink-0">
        <div>
          <h2 className="text-base font-black" style={{ color: '#0A192F' }}>Map View</h2>
          <p className="text-xs text-slate-400 mt-0.5">{filtered.length} listing{filtered.length !== 1 ? 's' : ''} shown</p>
        </div>
        <div className="flex items-center gap-2">
          {FILTERS.map(f => (
            <button key={f.key} onClick={() => setFilter(f.key)}
              data-testid={`map-filter-${f.key}`}
              className="px-3 py-1.5 rounded-full text-xs font-bold transition-all border"
              style={filter === f.key
                ? { background: f.color, color: 'white', borderColor: f.color }
                : { background: 'white', color: '#64748B', borderColor: '#E2E8F0' }}>
              {f.key !== 'all' && (
                <span className="inline-block w-2 h-2 rounded-full mr-1.5" style={{ background: f.color }} />
              )}
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Map */}
      <div className="flex-1 relative">
        {loading ? (
          <div className="absolute inset-0 flex items-center justify-center bg-slate-50">
            <div className="w-8 h-8 rounded-full animate-spin"
              style={{ border: '3px solid #0A192F', borderTopColor: 'transparent' }} />
          </div>
        ) : (
          <MapContainer
            center={[33.45, -111.85]}
            zoom={11}
            style={{ width: '100%', height: '100%' }}
            zoomControl={true}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://openstreetmap.org">OpenStreetMap</a>'
            />
            {filtered.length > 0 && <FitBounds markers={filtered} />}
            {filtered.map(({ listing, coords }) => (
              <CircleMarker
                key={listing.id}
                center={coords}
                radius={listing.match_score >= 80 ? 18 : listing.match_score >= 60 ? 15 : 12}
                pathOptions={{
                  fillColor: scoreColor(listing.match_score),
                  fillOpacity: 0.85,
                  color: 'white',
                  weight: 2,
                }}
                data-testid={`map-pin-${listing.id}`}
              >
                <Popup maxWidth={240} className="goldielocks-popup">
                  <div className="p-1 min-w-[200px]">
                    {/* Match Score */}
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-bold text-slate-600 truncate max-w-[130px]">{listing.city}, {listing.state}</span>
                      {listing.match_score != null && (
                        <span className="flex items-center gap-0.5 px-2 py-0.5 rounded-full text-xs font-black text-white"
                          style={{ background: scoreColor(listing.match_score) }}>
                          <Star size={9} fill="white" />{listing.match_score}%
                        </span>
                      )}
                    </div>
                    {/* Address */}
                    <p className="text-sm font-bold text-slate-800 leading-tight mb-1">{listing.address}</p>
                    {/* Price */}
                    <p className="text-base font-black mb-2" style={{ color: '#0A192F' }}>
                      ${listing.price?.toLocaleString()}
                    </p>
                    {/* Specs */}
                    <div className="flex items-center gap-3 text-xs text-slate-500 mb-3">
                      <span className="flex items-center gap-1"><BedDouble size={11} />{listing.beds} bd</span>
                      <span className="flex items-center gap-1"><Bath size={11} />{listing.baths} ba</span>
                      <span className="flex items-center gap-1"><Maximize2 size={11} />{listing.sqft?.toLocaleString()}</span>
                    </div>
                    {/* CTA */}
                    <button
                      onClick={() => navigate(`/listing/${listing.id}`, { state: { listing } })}
                      data-testid={`map-view-detail-${listing.id}`}
                      className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-bold text-white transition-all hover:opacity-90"
                      style={{ background: '#0A192F' }}>
                      <ExternalLink size={11} />View Details
                    </button>
                  </div>
                </Popup>
              </CircleMarker>
            ))}
          </MapContainer>
        )}

        {/* Legend */}
        <div className="absolute bottom-4 left-4 z-[1000] bg-white/95 backdrop-blur rounded-xl border border-slate-100 px-3 py-2.5 shadow-md">
          <p className="text-xs font-bold text-slate-600 mb-1.5">Match Score</p>
          {[
            { color: '#10B981', label: '80%+ Great match' },
            { color: '#F59E0B', label: '60-79% Good match' },
            { color: '#94A3B8', label: '<60% Partial match' },
          ].map(l => (
            <div key={l.color} className="flex items-center gap-2 text-xs text-slate-500 mb-1">
              <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: l.color }} />
              {l.label}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
